"use client";

import React from "react";
import { Icon } from "@iconify/react";
import parse from "html-react-parser";
import { motion } from "framer-motion";
import { Button } from "@heroui/button";

import useBlogCard from "./useBlogCard";

import { BlogCardProps } from "@/types";
import { ViewImage } from "@/components";

const BlogCard = ({ data }: BlogCardProps) => {
  const { categories, viewBlog } = useBlogCard();

  const category =
    categories?.find((cat: { _id: string }) => cat._id === data?.category)
      ?.name ?? "General";

  return (
    <motion.div
      className="
        group
        flex h-full flex-col
        overflow-hidden
        rounded-[28px]
        bg-background
      "
      initial={{ opacity: 0, y: 30 }}
      transition={{ duration: 0.45 }}
      viewport={{ once: true }}
      whileHover={{ y: -6 }}
      whileInView={{ opacity: 1, y: 0 }}
    >
      {/* image */}
      <div className="relative overflow-hidden rounded-[28px]">
        <motion.div
          className="overflow-hidden rounded-[28px]"
          transition={{ duration: 0.5 }}
          whileHover={{ scale: 1.05 }}
        >
          <ViewImage
            className="
              h-[220px] w-full
              rounded-[28px] object-cover
            "
            img={data?.featuredImage}
          />
        </motion.div>

        {/* category */}
        <div className="absolute left-4 top-4">
          <div
            className="
              rounded-full
              border border-white/20
              bg-white/80
              backdrop-blur-md
              px-3 py-1.5
              text-[10px] md:text-xs
              font-medium
              text-primary
              capitalize
              shadow-sm
            "
          >
            {category}
          </div>
        </div>
      </div>

      {/* spacing between image & content */}
      <div className="h-3" />

      {/* content */}
      <div
        className="
          flex flex-1 flex-col
          rounded-[28px]
          bg-primary/5
          p-4
          transition-colors duration-300
          group-hover:bg-primary/[0.07]
        "
      >
        {/* top content */}
        <div className="flex-1">
          {/* meta */}
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-neutral-500">
              {new Date(data?.createdAt as string).toDateString()}
            </div>

            {/* category */}
            <div
              className="
                rounded-full
                bg-primary/10
                px-2.5 py-1
                text-[10px]
                font-medium
                text-primary
                capitalize
              "
            >
              {category}
            </div>
          </div>

          {/* title */}
          <div className="mt-4 min-h-[56px]">
            <h2
              className="
                line-clamp-2
                text-base md:text-lg
                font-medium
                leading-7
                tracking-[-0.03em]
                text-primary
              "
            >
              {data?.title}
            </h2>
          </div>

          {/* content */}
          <div
            className="
              mt-4
              min-h-[96px]
              text-xs md:text-sm
              leading-6
              text-neutral-500
              line-clamp-4
            "
          >
            {typeof data?.content === "string"
              ? parse(
                  data?.content?.length > 220
                    ? data?.content?.slice(0, 220) + "..."
                    : data?.content,
                )
              : null}
          </div>
        </div>

        {/* footer */}
        <div className="mt-6">
          <Button
            className="
              bg-primary
              dark:bg-primary-50
              px-4
              text-xs font-medium
              text-white
              transition-transform duration-300
              group-hover:scale-[1.02]
            "
            color="primary"
            endContent={
              <motion.div
                transition={{ duration: 0.2 }}
                whileHover={{ x: 2, y: -2 }}
              >
                <Icon className="text-sm" icon="lucide:arrow-up-right" />
              </motion.div>
            }
            radius="full"
            size="sm"
            onPress={() => viewBlog(data?.slug as string)}
          >
            Read article
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default BlogCard;
